import {
  Bell,
  Clock,
  AlertTriangle,
  Trophy,
  GraduationCap,
  Megaphone,
  Sparkles,
  Users,
  CheckCircle2,
  Crown,
  CreditCard,
  AlertCircle,
} from "lucide-react";

/**
 * Format timestamp into relative or short readable format.
 */
export function formatNotificationTime(timestamp) {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) {
    return "just now";
  }
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m ago`;
  }
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h ago`;
  }
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  }

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

/**
 * Return configuration (icon, brand color classes, accent) for a notification type.
 * Uses ExamsPrep's core brand tokens:
 * - green02 (#DCEAE2) + green01 (#0C781C): 5.8:1 contrast
 * - gray03 (#E8EDF5) + blue02 (#0033FF): 6.4:1 contrast
 * - purple02 (#E6E2F0) + purple01 (#421F88): 8.2:1 contrast
 * - yellow01 (#FDEBCB) + amber-800 / yellow02
 * - rose-100 + red01 (#8D054F)
 */
export function getNotificationTypeConfig(type) {
  switch (type) {
    case "quiz_result":
      return {
        icon: Trophy,
        bgClass: "bg-green02 text-green01",
        borderClass: "border-green-200/80",
        label: "Quiz Result",
      };
    case "course_enrolled":
      return {
        icon: GraduationCap,
        bgClass: "bg-gray03 text-blue02",
        borderClass: "border-blue-200/70",
        label: "Course Enrolled",
      };
    case "quiz_reminder":
      return {
        icon: Clock,
        bgClass: "bg-yellow01 text-amber-800",
        borderClass: "border-amber-200",
        label: "Quiz Reminder",
      };
    case "quiz_deadline":
      return {
        icon: AlertTriangle,
        bgClass: "bg-amber-100 text-amber-900",
        borderClass: "border-amber-300",
        label: "Quiz Deadline",
      };
    case "general_announcement":
      return {
        icon: Megaphone,
        bgClass: "bg-gray03 text-blue01",
        borderClass: "border-blue-200",
        label: "Announcement",
      };
    case "marketing_promo":
      return {
        icon: Sparkles,
        bgClass: "bg-purple02 text-purple01",
        borderClass: "border-purple-200",
        label: "Promotion",
      };
    case "student_activity":
      return {
        icon: Users,
        bgClass: "bg-purple02 text-purple01",
        borderClass: "border-purple-200",
        label: "Student Activity",
      };
    case "quiz_completed_alert":
      return {
        icon: CheckCircle2,
        bgClass: "bg-green02 text-green01",
        borderClass: "border-green-200/80",
        label: "Quiz Completed",
      };
    case "subscription_activated":
      return {
        icon: Crown,
        bgClass: "bg-purple02 text-purple01",
        borderClass: "border-purple-200",
        label: "Subscription",
      };
    case "payment_successful":
      return {
        icon: CreditCard,
        bgClass: "bg-green02 text-green01",
        borderClass: "border-green-200/80",
        label: "Payment",
      };
    case "payment_failed":
    case "subscription_expiring_soon":
      return {
        icon: AlertCircle,
        bgClass: "bg-rose-100 text-red01",
        borderClass: "border-rose-200",
        label: "Billing Alert",
      };
    default:
      return {
        icon: Bell,
        bgClass: "bg-gray03 text-blue02",
        borderClass: "border-blue-200/60",
        label: "Notification",
      };
  }
}

/**
 * Return priority badge styling if high or urgent.
 */
export function getPriorityBadge(priority) {
  if (priority === "urgent") {
    return {
      text: "Urgent",
      className: "bg-red-500/10 text-red-600 border border-red-500/30 text-[10px] font-semibold px-1.5 py-0.5 rounded",
    };
  }
  if (priority === "high") {
    return {
      text: "High",
      className: "bg-amber-500/10 text-amber-700 border border-amber-500/30 text-[10px] font-semibold px-1.5 py-0.5 rounded",
    };
  }
  return null;
}
